import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ChevronRight } from "lucide-react";
import { useTranslation } from 'react-i18next';
import Link from "next/link";
import Image from "next/image";
import { cn, useFormatDateToDisplay } from "@/lib/utils";

type EventListItemProps = {
    title: string;
    slug: string;
    description: string;
    image_url: string;
    tags: {
        name: string;
    }[];
    xp: number;
    is_registered: boolean;
    timestamp: number;
};

export function EventListItem({ title, slug, description, image_url, tags, xp, is_registered, timestamp }: EventListItemProps) {
    const isPastEvent = new Date(timestamp * 1000).getTime() < Date.now();
    const { t, i18n } = useTranslation();
    const formatDate = useFormatDateToDisplay();
    
    const formatEventDateTime = (timestamp: number) => {
        const date = new Date(timestamp * 1000);
        const now = new Date();
        const isToday = date.toDateString() === now.toDateString();
        const isTomorrow = date.toDateString() === new Date(now.getTime() + 24 * 60 * 60 * 1000).toDateString();
        const timeString = formatDate(timestamp, 'time');

        if (isToday) {
            return `${t('events.today')} ${timeString}`;
        } else if (isTomorrow) {
            return `${t('events.tomorrow')} ${timeString}`;
        } else {
            return formatDate(timestamp);
        }
    };

    return (
        <div className={`relative w-full flex flex-col lg:flex-row items-start gap-4 border bg-muted/25 rounded-xl p-4 ${isPastEvent ? 'grayscale' : ''}`}>
            <Image src={image_url} alt="cover" className="h-36 w-full lg:w-auto object-cover rounded-md" width={400} height={200} />

            <div className="flex-1 w-full lg:w-auto flex flex-col min-h-[136px]">
                <div className="flex-grow">
                    <div className="flex justify-between items-center">
                        <h1 className="text-xl font-bold text-neutral-900 dark:text-neutral-100 max-lg:mb-2">{title}</h1>
                        <div className="flex gap-4">
                            <Badge variant="outline" className={cn("text-xs font-medium",
                                isPastEvent ? "bg-neutral-200 text-neutral-500 dark:bg-neutral-800 dark:text-neutral-400" : "bg-blue-600 text-white"
                            )}>
                                {formatEventDateTime(timestamp)}
                            </Badge>
                            {is_registered && !isPastEvent ? (
                                <Badge className="bg-green-600 text-white">
                                    {t('dispatch.registered')}
                                </Badge>
                            ) : null
                            }
                        </div>
                    </div>
                    <p className="text-sm text-neutral-600 dark:text-neutral-400 w-full lg:max-w-[75%] line-clamp-3 mb-2">
                        {description}
                    </p>
                </div>

                <div className="flex flex-col lg:flex-row lg:items-end justify-between w-full mt-auto">
                    <div className="flex flex-wrap gap-2 mb-2 lg:mb-0">
                        {(tags || []).map((tag, index) => (
                            <Badge key={index} className="bg-purple-600 text-white text-nowrap">
                                {tag.name}
                            </Badge>
                        ))}
                        <Badge className="bg-sky-600 text-white text-nowrap">{xp} XP</Badge>
                    </div>
                    <Button className={`w-full lg:w-auto mt-4 lg:mt-0 ${isPastEvent ? 'bg-neutral-200 text-neutral-500 dark:bg-neutral-800 dark:text-neutral-400' : ''}`}>
                        <Link href={`/community/events/${slug}`} className="flex items-center">
                            {t('dispatch.view_event')}
                            <ChevronRight className="ml-2 h-4 w-4" />
                        </Link>
                    </Button>
                </div>
            </div>
        </div>
    );
}